// Pure state-decision helpers for the public presentation share page's
// sticky send bar (2026-07-23 v2 — CEO "clicked send, nothing happened"
// fix). Extracted from PublicPresentationPage so the offline QA gate can
// EXECUTE the state machine (no React, no DOM, no network) rather than
// asserting on source regex. Keep this module dependency-free.
//
// 2026-07-26 (B6) — the bar now counts TWO signals: item picks ("N items
// selected") and notes ("M notes"). Either alone is enough to send, so the
// submit guard and the bar view both carry the selection count.

export type SubmitState = "idle" | "saving" | "done" | "error";

// The rust-bold line shown in the bar's left slot when the client clicks
// Send with nothing to send (zero non-empty notes). Lives here (not inline)
// so the gate and the component agree on the exact copy. 2026-07-27 — the
// client-facing item-selection pill was removed, so this is notes-only copy
// (validateSubmit keeps its dormant selection parameter for a future re-enable).
export const NEEDS_A_NOTE_MESSAGE = "Write a note first — then send.";

// What the send bar should render, derived purely from the submit state,
// the live counts of selected items + filled notes, and any active error
// line.
//   - "ready" — the default: selection count + note count + rust Send pill,
//     carrying the saving flag and any rust error line (needs-a-note OR an
//     API error).
//   - "sent"  — success: the ✓ Feedback sent + Send-more affordance. This
//     REPLACES the old full-page thank-you takeover for this flow.
export type BarView =
  | {
      kind: "ready";
      selectedCount: number;
      noteCount: number;
      saving: boolean;
      error: string | null;
    }
  | { kind: "sent" };

/**
 * Pure bar-state decision. Given the submit state, the live selection +
 * note counts, and any active error message, decide what the send bar
 * renders. On success (submitState === "done") the bar flips to the SENT
 * view; every other state renders READY, surfacing the saving flag and the
 * error line in the bar itself (never in the out-of-view page body).
 * `selectedCount` is optional so pre-B6 call shapes keep working (0 picks).
 */
export function deriveBarView(input: {
  submitState: SubmitState;
  noteCount: number;
  selectedCount?: number;
  error: string | null;
}): BarView {
  if (input.submitState === "done") {
    return { kind: "sent" };
  }
  return {
    kind: "ready",
    selectedCount: input.selectedCount ?? 0,
    noteCount: input.noteCount,
    saving: input.submitState === "saving",
    error: input.error,
  };
}

/**
 * Pure submit guard. Clicking Send with nothing to send must not silently
 * no-op (the reported bug) — it returns the needs-a-note error so the caller
 * can surface it in the bar's left slot. A submission is valid when the
 * client has EITHER picked at least one item OR written at least one note.
 * `selectedCount` defaults to 0 so the pre-B6 single-argument call still
 * behaves exactly as before (notes-only).
 */
export function validateSubmit(
  noteCount: number,
  selectedCount = 0,
): { ok: true } | { ok: false; error: string } {
  if (noteCount === 0 && selectedCount === 0) {
    return { ok: false, error: NEEDS_A_NOTE_MESSAGE };
  }
  return { ok: true };
}

/**
 * Pure "Send more feedback" reset. The just-sent notes + picks were delivered
 * (backend resubmission appends server-side, latest selection set wins), so
 * we clear the boxes and the picks and drop back to an idle/ready bar for a
 * fresh round.
 */
export function sendMoreState(): {
  submitState: SubmitState;
  itemComments: Record<string, string>;
  itemSelections: Record<string, boolean>;
  error: string | null;
} {
  return {
    submitState: "idle",
    itemComments: {},
    itemSelections: {},
    error: null,
  };
}
